import PocketBase, { AuthModel } from 'pocketbase'

const getBaseUrl = (): string => {
  return (import.meta.env.VITE_POCKETBASE_URL as string) || 'http://localhost:8090'
}

export const pb = new PocketBase(getBaseUrl())
pb.autoCancellation(false)

/**
 * When the signed-in person last actually used the app (a click, key, scroll
 * or touch), shared by every tab. The idle timeout (hooks/useSessionTimeout)
 * reads it. Only real input writes it, and a sign-in starts it afresh — so a
 * browser reopened the next morning is signed out, not resumed.
 */
export const LAST_ACTIVITY_KEY = 'lastActivity'
export function markActivity(): void {
  try { localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString()) } catch (err) { console.error('[session] could not record activity:', err) }
}
// Registered here, where pb is made, so it is in place before any sign-in.
let signedIn = pb.authStore.isValid
pb.authStore.onChange((token) => {
  if (token && !signedIn) markActivity()
  signedIn = !!token
})

// On a 401 response: attempt one token refresh before giving up.
// Only redirect to /login if the refresh itself confirms the token is dead.
// Transient 401s (Railway cold starts, race conditions) stay alive.
let redirecting401 = false
let refreshOn401Promise: Promise<void> | null = null

pb.afterSend = async function (response: Response, data: any) {
  if (response.status !== 401 || !pb.authStore.isValid || redirecting401) {
    return data
  }

  // The auth-refresh endpoint returning 401 means the token is genuinely dead — redirect now.
  // Guard must come before the refresh attempt to prevent an infinite loop.
  if (/\/auth-?refresh$/i.test(response.url ?? '')) {
    redirecting401 = true
    pb.authStore.clear()
    window.location.href = '/login?expired=true'
    return data
  }

  // Dedupe: multiple concurrent 401s share one refresh attempt.
  if (!refreshOn401Promise) {
    refreshOn401Promise = (async () => {
      try {
        await pb.collection('users').authRefresh()
      } catch (err: any) {
        const status = Number(err?.status || err?.response?.status || 0)
        // 401 or 400 from the refresh endpoint = token is genuinely invalid.
        if ((status === 401 || status === 400) && !redirecting401) {
          redirecting401 = true
          pb.authStore.clear()
          window.location.href = '/login?expired=true'
        }
        // Network error / 5xx / 429: keep the session alive, surface error to callers.
      } finally {
        refreshOn401Promise = null
      }
    })()
  }

  // All concurrent 401s wait here until the single refresh resolves.
  await refreshOn401Promise
  return data
}

// Combine two AbortSignals so whichever fires first aborts the combined signal
function combineSignals(a: AbortSignal, b: AbortSignal): AbortSignal {
  const controller = new AbortController()
  const abort = () => controller.abort()
  if (a.aborted || b.aborted) {
    controller.abort()
  } else {
    a.addEventListener('abort', abort, { once: true })
    b.addEventListener('abort', abort, { once: true })
  }
  return controller.signal
}

// Apply a 30-second hard timeout to every PocketBase request.
// Without this, stalled TCP connections on mobile hang indefinitely and
// React Query's retry logic never fires because the request never errors.
// 30s covers Railway cold starts (~15-25s) while still catching truly stalled connections.
pb.beforeSend = function (url, options) {
  // A token past its expiry is a dead session, and this is the only place it
  // gets caught. PocketBase answers a list request carrying a dead token with
  // 200 and an empty page — the record rules match nothing — rather than 401,
  // so afterSend's refresh-and-redirect below never runs. The app then renders
  // an empty book: no clients, no contracts, $0 of money, and nothing on
  // screen saying the session ended. Those figures are not stale, they are
  // absent, and on a money tool that is the worst way to be wrong.
  //
  // Auth endpoints are exempt: signing in and refreshing legitimately carry a
  // dead or missing token, and bouncing them would make recovery impossible.
  if (
    pb.authStore.token &&
    !pb.authStore.isValid &&
    !redirecting401 &&
    !/\/(auth-\w+|request-password-reset|confirm-\w+)/i.test(url)
  ) {
    redirecting401 = true
    pb.authStore.clear()
    window.location.href = '/login?expired=true'
  }

  const timeout = new AbortController()
  setTimeout(() => timeout.abort(new Error('Request timeout')), 30_000)
  const signal = options.signal
    ? combineSignals(options.signal as AbortSignal, timeout.signal)
    : timeout.signal
  return { url, options: { ...options, signal } }
}

const AUTH_REFRESH_MIN_INTERVAL_MS = 30_000
let authRefreshPromise: Promise<AuthModel | null> | null = null
let authRefreshLastAt = 0
let authRefreshBlockedUntil = 0

// PocketBase v0.30 rotates tokens on every authRefresh call. Only call authRefresh
// when the token is genuinely near expiry — not on every focus or page load.
const TOKEN_NEAR_EXPIRY_SECONDS = 300 // 5 minutes

function isTokenNearExpiry(): boolean {
  const token = pb.authStore.token
  if (!token) return true
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    return !payload.exp || (payload.exp - Math.floor(Date.now() / 1000)) < TOKEN_NEAR_EXPIRY_SECONDS
  } catch {
    return true
  }
}

function getErrorStatus(error: any): number {
  return Number(error?.status || error?.response?.status || 0)
}

function parseRetryAfterSeconds(error: any): number | null {
  const headerValue =
    error?.response?.headers?.['retry-after'] ||
    error?.response?.headers?.get?.('retry-after')

  const fromHeader = Number.parseInt(String(headerValue || ''), 10)
  if (Number.isFinite(fromHeader) && fromHeader > 0) {
    return fromHeader
  }

  const text = String(
    error?.response?.data?.message ||
    error?.response?.message ||
    error?.message ||
    ''
  )
  const match = text.match(/retry(?:\s|-)?after[^0-9]*(\d+)/i)
  if (match) {
    const seconds = Number.parseInt(match[1], 10)
    if (Number.isFinite(seconds) && seconds > 0) {
      return seconds
    }
  }

  return null
}

export function getCurrentUser(): AuthModel | null {
  return pb.authStore.model as AuthModel | null
}

export function onAuthChange(callback: (user: AuthModel | null) => void) {
  return pb.authStore.onChange(() => callback(pb.authStore.model as AuthModel | null))
}

export async function login(email: string, password: string) {
  const auth = await pb.collection('users').authWithPassword(email, password)
  return auth.record as AuthModel
}

export async function register(
  email: string,
  password: string,
  name?: string | null
) {
  // Split name into first_name and last_name if provided
  let firstName = ''
  let lastName = ''
  if (name) {
    const parts = name.trim().split(' ')
    firstName = parts[0] || ''
    lastName = parts.slice(1).join(' ') || ''
  }

  const userPayload: any = {
    email,
    password,
    passwordConfirm: password,
  }

  // Only add name fields if provided
  if (firstName) {
    userPayload.first_name = firstName
  }
  if (lastName) {
    userPayload.last_name = lastName
  }

  const record = await pb.collection('users').create(userPayload)

  // Auto-login after successful registration
  await pb.collection('users').authWithPassword(email, password)
  return record as AuthModel
}

export function logout() {
  pb.authStore.clear()
}

/**
 * Login with Google OAuth
 * Opens a popup for Google authentication
 */
export async function loginWithGoogle() {
  await pb.collection('users').authWithOAuth2({ provider: 'google' })
  return pb.authStore.model as AuthModel
}

/**
 * Login with Microsoft (Entra ID / Microsoft 365) OAuth. The provider is
 * configured once in the PocketBase admin UI for the whole instance; see
 * docs/microsoft-sign-in.md. Which agency the account lands in is decided
 * afterwards by team sign-in (api/join-by-domain.js), not by the provider.
 */
export async function loginWithMicrosoft() {
  await pb.collection('users').authWithOAuth2({ provider: 'microsoft' })
  return pb.authStore.model as AuthModel
}

/**
 * The single sign-on providers this PocketBase instance actually has
 * configured. The sign-in page offers only these, so a button never opens a
 * popup that comes back "provider not configured" — and a provider added in
 * the admin UI appears without a code change. Throws if the list cannot be
 * read; the caller says so rather than guessing at what works.
 */
export async function listAuthProviders(): Promise<('google' | 'microsoft')[]> {
  // PocketBase 0.30 answers { oauth2: { providers: [...] } }; the SDK bundled
  // here (0.21) is typed for the older { authProviders: [...] }. Both shapes
  // are read so the answer comes from the server either way.
  const methods = await pb.collection('users').listAuthMethods() as unknown as {
    oauth2?: { providers?: { name?: string }[] }
    authProviders?: { name?: string }[]
  }
  const names = (methods.oauth2?.providers ?? methods.authProviders ?? []).map(p => String(p?.name ?? ''))
  return (['google', 'microsoft'] as const).filter(p => names.includes(p))
}

/**
 * Fetch the current user's latest record from PocketBase without rotating the token.
 * Use this instead of safeAuthRefresh when you need fresh user data (updated role,
 * agency_id, etc.) — it does a direct getOne and saves the result into authStore
 * so onChange listeners fire, but never calls authRefresh.
 */
export async function fetchFreshUser(): Promise<AuthModel | null> {
  if (!pb.authStore.isValid) return null
  const userId = (pb.authStore.model as any)?.id
  if (!userId) return null
  try {
    const fresh = await pb.collection('users').getOne(userId, { requestKey: null })
    // PocketBase hides some fields (e.g. agency_id) from getOne but returns them via
    // authWithPassword. Merge so fresh fields update without losing hidden ones.
    const existing = (pb.authStore.model ?? {}) as Record<string, unknown>
    const merged = { ...existing, ...fresh }
    pb.authStore.save(pb.authStore.token, merged)
    return merged as AuthModel
  } catch {
    return pb.authStore.model as AuthModel | null
  }
}

/**
 * Refresh the current user's auth data from the server.
 * Uses fetchFreshUser (no token rotation) instead of authRefresh.
 */
export async function refreshAuth() {
  return fetchFreshUser()
}

/**
 * Refresh auth with in-flight dedupe + 429 backoff to prevent
 * refresh storms from multiple components loading in parallel.
 */
export async function safeAuthRefresh(options?: {
  force?: boolean
  minIntervalMs?: number
}): Promise<AuthModel | null> {
  if (!pb.authStore.isValid) {
    return null
  }

  const force = options?.force ?? false
  const minIntervalMs = options?.minIntervalMs ?? AUTH_REFRESH_MIN_INTERVAL_MS
  const now = Date.now()

  if (!force) {
    if (now < authRefreshBlockedUntil) {
      return pb.authStore.model as AuthModel | null
    }
    if (now - authRefreshLastAt < minIntervalMs) {
      return pb.authStore.model as AuthModel | null
    }
  }

  if (authRefreshPromise) {
    return authRefreshPromise
  }

  authRefreshPromise = (async () => {
    // PocketBase v0.30 rotates tokens on every authRefresh call. Skip unless near expiry
    // to prevent concurrent requests from getting 401 with the just-rotated old token.
    if (!isTokenNearExpiry()) {
      authRefreshPromise = null
      return pb.authStore.model as AuthModel | null
    }

    // Timeout prevents hanging if PocketHost is unreachable
    const timeout = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Auth refresh network timeout')), 10_000)
    )
    try {
      await Promise.race([
        pb.collection('users').authRefresh(),
        timeout,
      ])
      authRefreshLastAt = Date.now()
      authRefreshBlockedUntil = 0
      return pb.authStore.model as AuthModel | null
    } catch (err: any) {
      const status = getErrorStatus(err)
      if (status === 429) {
        const retryAfterSeconds = parseRetryAfterSeconds(err) ?? 60
        authRefreshBlockedUntil = Date.now() + retryAfterSeconds * 1000
        console.warn(`Auth refresh rate-limited. Backing off for ${retryAfterSeconds}s.`)
        return pb.authStore.model as AuthModel | null
      }
      if (status === 401 || status === 404) {
        pb.authStore.clear()
        return null
      }
      return pb.authStore.model as AuthModel | null
    } finally {
      authRefreshPromise = null
    }
  })()

  return authRefreshPromise
}

/**
 * Escape a value for safe use in PocketBase filter strings.
 * Prevents filter injection attacks by escaping special characters.
 *
 * @example
 * // Safe usage:
 * const filter = `email = "${escapeFilterValue(userInput)}"`
 *
 * // Instead of vulnerable:
 * const filter = `email = "${userInput}"` // DANGEROUS!
 */
export function escapeFilterValue(value: string): string {
  if (!value) return ''
  // Escape backslashes first, then double quotes
  // PocketBase uses double quotes for string values in filters
  return value
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
}
